import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle, DialogDescription } from '@/components/ui/Dialog';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Icon from '@/components/AppIcon';
import { Switch } from '@/components/ui/Switch';
import { getPolicyLeaveTypes, savePolicyLeaveType, deletePolicyLeaveType, getLeaveTypeBalanceCount } from '../../../../../api/Attendance/leaves';
import type { LeavePolicyView, LeaveTypeView, LeaveTypeInput } from '../../../../../models/attendance';
import { getApiErrorMessage } from '../../../../../utils/apiErrors';

interface LeaveTypesTabProps {
  currentPolicy?: LeavePolicyView;
}

const LeaveTypesTab: React.FC<LeaveTypesTabProps> = ({ currentPolicy }) => {
  const { t } = useTranslation('leaves');
  const [leaveTypes, setLeaveTypes] = useState<LeaveTypeView[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [editingType, setEditingType] = useState<LeaveTypeView | null>(null);
  const [newLeaveType, setNewLeaveType] = useState<LeaveTypeInput>({
    name: '', code: '', color: '#10B981', icon: 'CalendarDays', description: '', isActive: true,
    minNoticeDays: 0, maxConsecutiveDays: undefined, sandwichRuleEnabled: false
  });
  const [isSaving, setIsSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<LeaveTypeView | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletePhase, setDeletePhase] = useState<'confirm' | 'force'>('confirm');
  const [affectedCount, setAffectedCount] = useState<number | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    if (currentPolicy?.id) loadLeaveTypes();
  }, [currentPolicy?.id]);

  const loadLeaveTypes = async () => {
    if (!currentPolicy?.id) return;
    setIsLoading(true);
    try {
      const result = await getPolicyLeaveTypes(currentPolicy.id);
      if (result?.errors?.length) {
        setError(getApiErrorMessage(result, t, t('Failed to load leave types')));
      } else {
        setError(null);
        setLeaveTypes(result.payload ?? []);
      }
    } catch (err) {
      setError(getApiErrorMessage(err, t, t('Failed to load leave types')));
    } finally {
      setIsLoading(false);
    }
  };

  const colorOptions = [
    { value: '#10B981', label: t('Green') },
    { value: '#3B82F6', label: t('Blue') },
    { value: '#EF4444', label: t('Red') },
    { value: '#F59E0B', label: t('Orange') },
    { value: '#8B5CF6', label: t('Purple') },
    { value: '#EC4899', label: t('Pink') },
    { value: '#6B7280', label: t('Gray') }
  ];

  const iconOptions = [
    { value: 'CalendarDays', label: t('Calendar') },
    { value: 'Heart', label: t('Heart') },
    { value: 'User', label: t('User') },
    { value: 'Baby', label: t('Baby') },
    { value: 'Briefcase', label: t('Briefcase') },
    { value: 'GraduationCap', label: t('Education') },
    { value: 'Plane', label: t('Travel') }
  ];

  const handleAddLeaveType = async (): Promise<void> => {
    if (!currentPolicy?.id) {
      showToast(t('Please create a leave policy before adding leave types'), 'error');
      return;
    }
    if (!newLeaveType?.name || !newLeaveType?.code) return;
    setIsSaving(true);
    try {
      const result = await savePolicyLeaveType(currentPolicy.id, { ...newLeaveType });
      if (!result?.errors?.length) {
        await loadLeaveTypes();
        resetForm();
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditLeaveType = (type: LeaveTypeView): void => {
    setEditingType(type);
    setNewLeaveType({
      name: type.name,
      code: type.code || getDisplayCode(type),
      color: type.color || DEFAULT_COLOR,
      icon: type.icon || DEFAULT_ICON,
      description: type.description,
      isActive: type.isActive,
      minNoticeDays: type.minNoticeDays ?? 0,
      maxConsecutiveDays: type.maxConsecutiveDays ?? undefined,
      sandwichRuleEnabled: type.sandwichRuleEnabled ?? false
    });
    setShowAddModal(true);
  };

  const handleUpdateLeaveType = async (): Promise<void> => {
    if (!currentPolicy?.id || !editingType) return;
    setIsSaving(true);
    try {
      const result = await savePolicyLeaveType(currentPolicy.id, {
        ...newLeaveType,
        id: editingType.id
      });
      if (!result?.errors?.length) {
        await loadLeaveTypes();
        resetForm();
      }
    } finally {
      setIsSaving(false);
    }
  };

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  /**
   * The "this type is still in use" refusal, whether it arrives as a resolved Result carrying
   * errors[] or as a REJECTION. The server answers this particular refusal with HTTP 400, and the
   * shared request interceptor rejects any non-2xx with the parsed body — so the escalation used to
   * be unreachable: every attempt landed in the catch below and showed a generic "failed to delete"
   * toast, leaving no way to reach Force Delete from the UI at all.
   *
   * Sniffing the RAW server description keeps this read separate from the user-facing string the
   * shared helper builds.
   */
  const isInUseRefusal = (payload: any): boolean => {
    const description = payload?.errors?.[0]?.description ?? '';
    return typeof description === 'string'
      && description.toLowerCase().includes('active employee leave balances');
  };

  const escalateToForceDelete = async (): Promise<void> => {
    // The count is context for the warning, not a precondition for it. If it cannot be read we
    // still show the force step — losing the number is far better than dropping the user back to
    // a dead end with no way to complete the delete.
    let count = 0;
    try {
      const countResult = await getLeaveTypeBalanceCount(currentPolicy!.id!, deleteTarget!.id);
      count = countResult?.errors?.length ? 0 : (countResult.payload ?? 0);
    } catch {
      count = 0;
    }
    setAffectedCount(count);
    setDeletePhase('force');
  };

  const handleDeleteLeaveType = async (): Promise<void> => {
    if (!currentPolicy?.id || !deleteTarget) return;
    setIsDeleting(true);
    try {
      const result = await deletePolicyLeaveType(currentPolicy.id, deleteTarget.id);
      if (!result?.errors?.length) {
        setLeaveTypes(leaveTypes.filter(type => type.id !== deleteTarget.id));
        closeDeleteDialog();
        showToast(t('Leave type deleted successfully'), 'success');
      } else if (isInUseRefusal(result)) {
        await escalateToForceDelete();
      } else {
        closeDeleteDialog();
        showToast(getApiErrorMessage(result, t, t('Failed to delete leave type')), 'error');
      }
    } catch (err: any) {
      if (isInUseRefusal(err)) {
        await escalateToForceDelete();
      } else {
        closeDeleteDialog();
        showToast(getApiErrorMessage(err, t, t('Failed to delete leave type')), 'error');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleForceDeleteLeaveType = async (): Promise<void> => {
    if (!currentPolicy?.id || !deleteTarget) return;
    setIsDeleting(true);
    try {
      const result = await deletePolicyLeaveType(currentPolicy.id, deleteTarget.id, { forceDelete: true });
      if (!result?.errors?.length) {
        setLeaveTypes(leaveTypes.filter(type => type.id !== deleteTarget.id));
        closeDeleteDialog();
        showToast(t('Leave type force deleted successfully'), 'success');
      } else {
        closeDeleteDialog();
        showToast(getApiErrorMessage(result, t, t('Failed to delete leave type')), 'error');
      }
    } catch (err: any) {
      closeDeleteDialog();
      showToast(getApiErrorMessage(err, t, t('Failed to delete leave type')), 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const closeDeleteDialog = () => {
    setDeleteTarget(null);
    setDeletePhase('confirm');
    setAffectedCount(null);
  };

  const toggleLeaveTypeStatus = async (type: LeaveTypeView): Promise<void> => {
    if (!currentPolicy?.id) return;
    const result = await savePolicyLeaveType(currentPolicy.id, {
      id: type.id,
      name: type.name,
      code: type.code || getDisplayCode(type),
      color: type.color || DEFAULT_COLOR,
      icon: type.icon || DEFAULT_ICON,
      description: type.description,
      isActive: !type.isActive,
      minNoticeDays: type.minNoticeDays ?? 0,
      maxConsecutiveDays: type.maxConsecutiveDays ?? undefined,
      sandwichRuleEnabled: type.sandwichRuleEnabled ?? false
    });
    if (!result?.errors?.length) {
      setLeaveTypes(leaveTypes.map(lt =>
        lt.id === type.id ? { ...lt, isActive: !lt.isActive } : lt
      ));
    }
  };

  const resetForm = (): void => {
    setShowAddModal(false);
    setEditingType(null);
    setNewLeaveType({
      name: '', code: '', color: '#10B981', icon: 'CalendarDays', description: '', isActive: true,
      minNoticeDays: 0, maxConsecutiveDays: undefined, sandwichRuleEnabled: false
    });
  };

  const DEFAULT_COLOR = '#10B981';
  const DEFAULT_ICON = 'CalendarDays';

  const getDisplayCode = (type: LeaveTypeView): string => {
    if (type.code) return type.code;
    if (type.name) {
      return type.name
        .split(/\s+/)
        .filter(Boolean)
        .map(w => w[0])
        .join('')
        .toUpperCase()
        .slice(0, 3);
    }
    return '\u2014';
  };

  const getDisplayColor = (type: LeaveTypeView): string => type.color || DEFAULT_COLOR;
  const getDisplayIcon = (type: LeaveTypeView): string => type.icon || DEFAULT_ICON;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-foreground">{t('Leave Type Management')}</h3>
          <p className="text-sm text-muted-foreground">{t('Configure different types of leave with custom rules and policies')}</p>
        </div>
        <Button variant="default" iconName="Plus" onClick={() => setShowAddModal(true)} disabled={!currentPolicy?.id} data-testid="add-leave-type-btn">
          {t('Add Leave Type')}
        </Button>
      </div>

      {!currentPolicy?.id && (
        <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
          <div className="flex items-start space-x-3 rtl:space-x-reverse">
            <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-1">{t('No Leave Policy Selected')}</h3>
              <p className="text-sm text-foreground">
                {t('You need to create a leave policy before you can manage leave types. Use the "New Policy" button above to get started.')}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
        <div className="flex items-start space-x-3 rtl:space-x-reverse">
          <Icon name="Info" size={20} className="text-primary mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-1">{t('Leave Types Configuration')}</h3>
            <p className="text-sm text-foreground">
              {t('Define and manage leave types available in your organization. Each leave type can have specific rules, colors, and settings.')}
            </p>
          </div>
        </div>
      </div>

      {currentPolicy && (
        <div className="bg-muted border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Shield" size={16} className="text-primary" />
            <span className="text-sm font-semibold text-foreground">{t('Current Policy')}: {currentPolicy?.name}</span>
          </div>
          <div className="text-xs text-muted-foreground">
            <span>{currentPolicy?.legalEntity} &bull; {currentPolicy?.country} &bull; {currentPolicy?.employmentType}</span>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center space-x-2 rtl:space-x-reverse">
            <Icon name="AlertCircle" size={16} className="text-destructive" />
            <span className="text-sm text-destructive">{error}</span>
          </div>
          <Button variant="outline" size="sm" onClick={loadLeaveTypes}>{t('Retry')}</Button>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : leaveTypes.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="CalendarDays" size={48} className="text-muted-foreground/50 mx-auto mb-4" />
          <p className="text-muted-foreground">{t('No leave types configured yet.')}</p>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {leaveTypes?.map((type) => (
            <div key={type?.id} className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow" data-testid="leave-type-card">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3 rtl:space-x-reverse">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${getDisplayColor(type)}20` }}>
                    <Icon name={getDisplayIcon(type)} size={20} style={{ color: getDisplayColor(type) }} />
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground">{type?.name}</h4>
                    <span className="text-xs text-muted-foreground font-mono">{getDisplayCode(type)}</span>
                  </div>
                </div>
                <Switch
                  checked={type?.isActive ?? false}
                  onCheckedChange={() => toggleLeaveTypeStatus(type)}
                  data-testid="leave-type-toggle-status"
                />
              </div>

              <p className="text-sm text-muted-foreground mb-4">{type?.description}</p>

              <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                <Button variant="ghost" size="sm" iconName="Edit" onClick={() => handleEditLeaveType(type)} data-testid="leave-type-edit-btn">
                  {t('Edit')}
                </Button>
                <Button variant="ghost" size="sm" iconName="Trash2" onClick={() => setDeleteTarget(type)} className="text-destructive hover:text-destructive" data-testid="leave-type-delete-btn">
                  {t('Delete')}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) closeDeleteDialog(); }}>
        <DialogContent size="sm" data-testid="delete-leave-type-dialog">
          {deletePhase === 'confirm' ? (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
                    <Icon name="AlertTriangle" size={20} className="text-destructive" />
                  </div>
                  <DialogTitle>{t('Delete Leave Type')}</DialogTitle>
                </div>
              </DialogHeader>
              <DialogBody>
                <DialogDescription>
                  {t('Are you sure you want to delete')} <span className="font-medium text-foreground">{deleteTarget?.name}</span>? {t('This action cannot be undone.')}
                </DialogDescription>
              </DialogBody>
              <DialogFooter>
                <Button variant="outline" onClick={closeDeleteDialog} disabled={isDeleting} data-testid="delete-leave-type-cancel">
                  {t('Cancel')}
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDeleteLeaveType}
                  disabled={isDeleting}
                  loading={isDeleting}
                  data-testid="delete-leave-type-confirm"
                >
                  {t('Delete')}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-warning/10 flex items-center justify-center">
                    <Icon name="AlertTriangle" size={20} className="text-warning" />
                  </div>
                  <DialogTitle>{t('Force Delete Leave Type')}</DialogTitle>
                </div>
              </DialogHeader>
              <DialogBody>
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    {t('This leave type has')} <span className="font-semibold text-foreground">{affectedCount}</span> {t('active employee balance records')}.
                  </p>
                  <div className="bg-warning/10 border border-warning/20 rounded-lg p-3">
                    <p className="text-sm text-warning">
                      {t('Force deleting will archive all balances, remove accrual entitlements, and cancel pending leave requests for this type.')}
                    </p>
                  </div>
                </div>
              </DialogBody>
              <DialogFooter>
                <Button variant="outline" onClick={closeDeleteDialog} disabled={isDeleting} data-testid="delete-leave-type-cancel">
                  {t('Cancel')}
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleForceDeleteLeaveType}
                  disabled={isDeleting}
                  loading={isDeleting}
                  data-testid="delete-leave-type-force-confirm"
                >
                  {t('Force Delete')}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Toast notification */}
      {toast && (
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-lg shadow-lg text-sm font-medium flex items-center gap-2 transition-all ${
          toast.type === 'success' ? 'bg-success text-success-foreground' : 'bg-destructive text-destructive-foreground'
        }`}>
          <Icon name={toast.type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={16} />
          {toast.message}
          <button onClick={() => setToast(null)} className="ms-3 hover:opacity-80">
            <Icon name="X" size={14} />
          </button>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={showAddModal} onOpenChange={(open) => { if (!open) resetForm(); }}>
        <DialogContent size="lg" data-testid="leave-type-modal">
          <DialogHeader>
            <DialogTitle>
              {editingType ? t('Edit Leave Type') : t('Add New Leave Type')}
            </DialogTitle>
          </DialogHeader>

          <DialogBody className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">{t('Leave Type Name')}</label>
                <Input
                  type="text"
                  value={newLeaveType?.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewLeaveType({ ...newLeaveType, name: e?.target?.value })}
                  placeholder={t('e.g., Maternity Leave')}
                  data-testid="leave-type-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">{t('Code')}</label>
                <Input
                  type="text"
                  value={newLeaveType?.code}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewLeaveType({ ...newLeaveType, code: e?.target?.value?.toUpperCase() })}
                  placeholder={t('e.g., ML')}
                  maxLength={3}
                  data-testid="leave-type-code"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">{t('Description')}</label>
              <textarea
                value={newLeaveType?.description}
                onChange={(e) => setNewLeaveType({ ...newLeaveType, description: e?.target?.value })}
                placeholder={t('Describe this leave type...')}
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-md focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary focus-visible:outline-none bg-card text-foreground"
                data-testid="leave-type-description"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">{t('Color')}</label>
                <Select
                  value={newLeaveType?.color}
                  onChange={(value: string) => setNewLeaveType({ ...newLeaveType, color: value })}
                  options={colorOptions}
                  data-testid="leave-type-color"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">{t('Icon')}</label>
                <Select
                  value={newLeaveType?.icon}
                  onChange={(value: string) => setNewLeaveType({ ...newLeaveType, icon: value })}
                  options={iconOptions}
                  data-testid="leave-type-icon"
                />
              </div>
            </div>

            {/* Validation Rules */}
            <div className="border border-border rounded-lg p-4 space-y-4">
              <div className="flex items-center gap-2 mb-1">
                <Icon name="Shield" size={16} className="text-warning" />
                <h4 className="text-sm font-semibold text-foreground">{t('Validation Rules')}</h4>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">{t('Minimum Notice Days')}</label>
                  <Input
                    type="number"
                    min={0}
                    value={newLeaveType.minNoticeDays ?? 0}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewLeaveType({ ...newLeaveType, minNoticeDays: parseInt(e.target.value) || 0 })}
                    data-testid="leave-type-min-notice-days"
                  />
                  <p className="text-xs text-muted-foreground mt-1">{t('Days in advance a request must be submitted')}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">{t('Max Consecutive Days')}</label>
                  <Input
                    type="number"
                    min={1}
                    value={newLeaveType.maxConsecutiveDays ?? ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewLeaveType({ ...newLeaveType, maxConsecutiveDays: e.target.value ? parseInt(e.target.value) : undefined })}
                    placeholder={t('No limit')}
                    data-testid="leave-type-max-consecutive-days"
                  />
                  <p className="text-xs text-muted-foreground mt-1">{t('Maximum consecutive leave days allowed')}</p>
                </div>
              </div>

              <div className="flex items-start pt-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newLeaveType.sandwichRuleEnabled ?? false}
                    onChange={(e) => setNewLeaveType({ ...newLeaveType, sandwichRuleEnabled: e.target.checked })}
                    className="me-2 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    data-testid="leave-type-sandwich-rule"
                  />
                  <div>
                    <span className="text-sm font-medium text-foreground">{t('Sandwich Rule')}</span>
                    <p className="text-xs text-muted-foreground">{t('Count weekends between leave days')}</p>
                  </div>
                </label>
              </div>
            </div>
          </DialogBody>

          <DialogFooter>
            <Button variant="outline" onClick={resetForm} data-testid="leave-type-modal-cancel">{t('Cancel')}</Button>
            <Button
              variant="default"
              onClick={editingType ? handleUpdateLeaveType : handleAddLeaveType}
              disabled={!newLeaveType?.name || !newLeaveType?.code || isSaving}
              data-testid="leave-type-save-btn"
            >
              {isSaving ? t('Saving...') : `${editingType ? t('Update') : t('Add')} ${t('Leave Type')}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeaveTypesTab;
